import { isEqual } from "lodash-es";
import React, { FC, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { Settings } from "../types/settings";
import { SettingsContext, initialSettings } from "./settings-context-definition";

const STORAGE_KEY = "app.settings";

let storage: Storage | null = null;

class MemoryStorage implements Storage {
  store = new Map();

  get length(): number {
    return this.store.size;
  }

  clear(): void {
    this.store.clear();
  }

  getItem(key: string): string | null {
    return this.store.get(key);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  key(index: number): string | null {
    return Array.from(this.store.values())[index] || null;
  }
}

const getStorage = (): Storage => {
  if (storage !== null) return storage;

  if (typeof window === "undefined") {
    storage = new MemoryStorage();
    return storage;
  }

  try {
    storage = globalThis.localStorage;
  } catch (err) {
    console.error("[Settings Context] Local storage is not available", err);
    storage = new MemoryStorage();
  }
  return storage;
};

const restoreSettings = (): Settings | null => {
  let value = null;

  try {
    const restored: string | null = getStorage().getItem(STORAGE_KEY);

    if (restored) {
      value = JSON.parse(restored);
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return value;
};

const deleteSettings = (): void => {
  getStorage().removeItem(STORAGE_KEY);
};

const storeSettings = (value: Record<string, any>): void => {
  getStorage().setItem(STORAGE_KEY, JSON.stringify(value));
};

interface State extends Settings {
  isInitialized: boolean;
}

const initialState: State = {
  ...initialSettings,
  isInitialized: false,
};

interface SettingsProviderProps {
  children?: ReactNode;
}

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [state, setState] = useState<State>(initialState);

  useEffect(() => {
    const restored = restoreSettings();

    if (restored) {
      // oxlint-disable-next-line react/set-state-in-effect -- ENG-4333: fix and remove this disable
      setState((prevState) => ({
        ...prevState,
        ...restored,
        isInitialized: true,
      }));
    }
  }, []);

  const handleReset = useCallback((): void => {
    deleteSettings();
    setState((prevState) => ({
      ...prevState,
      ...initialSettings,
    }));
  }, []);

  const handleUpdate = useCallback((settings: Settings): void => {
    setState((prevState) => {
      storeSettings({
        paletteMode: prevState.paletteMode,
        pinNav: prevState.pinNav,
        rowCount: prevState.rowCount,
        ...settings,
      });

      return {
        ...prevState,
        ...settings,
      };
    });
  }, []);

  const isCustom = useMemo(() => {
    return !isEqual(initialSettings, {
      paletteMode: state.paletteMode,
      pinNav: state.pinNav,
      rowCount: state.rowCount,
    });
  }, [state]);

  return (
    <SettingsContext.Provider
      value={{
        ...state,
        handleReset,
        handleUpdate,
        isCustom,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export {
  SettingsContext,
  SettingsConsumer,
  type SettingsContextType,
} from "./settings-context-definition";
